/* =====================================================================
 * Skeleton — 로딩 스켈레톤 UI
 * ===================================================================== */

import React, { useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import gsap from 'gsap';
import { ScrambleTextPlugin } from 'gsap/ScrambleTextPlugin';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Base = styled.div<{
  $width?: string;
  $height?: string;
  $radius?: string;
  $mb?: string;
}>`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.bgHover} 25%,
    ${({ theme }) => theme.colors.bgSecondary} 50%,
    ${({ theme }) => theme.colors.bgHover} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${({ $radius }) => $radius || '8px'};
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '16px'};
  ${({ $mb }) =>
    $mb &&
    css`
      margin-bottom: ${$mb};
    `}
`;

/* 텍스트 라인 */
export const SkeletonLine: React.FC<{
  width?: string;
  height?: string;
  mb?: string;
  radius?: string;
}> = ({ width, height, mb, radius }) => (
  <Base $width={width} $height={height || '14px'} $radius={radius || '4px'} $mb={mb} />
);

/* 원형 (아바타 등) */
export const SkeletonCircle: React.FC<{ size?: string }> = ({ size = '32px' }) => (
  <Base $width={size} $height={size} $radius="50%" />
);

/* 메시지 스켈레톤 */
const MessageSkeletonWrapper = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 0;
`;

const Lines = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const MessageSkeleton: React.FC = () => (
  <MessageSkeletonWrapper>
    <SkeletonCircle size="32px" />
    <Lines>
      <SkeletonLine width="30%" height="14px" />
      <SkeletonLine width="80%" height="14px" />
      <SkeletonLine width="60%" height="14px" />
    </Lines>
  </MessageSkeletonWrapper>
);

/**
 * 채팅 로딩 스켈레톤을 실제 버블 칼럼과 같은 폭으로 가운데 정렬하는 래퍼.
 * max-width 는 버블(`Row`)과 동일한 `maxBubbleWidth` 토큰을 단일 출처로 사용해,
 * 로딩→실제 버블 전환 시 폭이 어긋나지 않게 한다. (이전에는 각 채팅 화면이
 * `maxWidth: '768px'`/`'860px'` 를 인라인 하드코딩해 토큰과 드리프트 위험이 있었다.)
 */
export const SkeletonColumn = styled.div`
  padding: 0 16px;
  max-width: ${({ theme }) => theme.sizes.maxBubbleWidth};
  margin: 0 auto;
`;

/* 사이드바 아이템 스켈레톤 */
const SidebarItemWrapper = styled.div`
  padding: 10px 12px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const SidebarItemSkeleton: React.FC = () => (
  <SidebarItemWrapper>
    <SkeletonCircle size="20px" />
    <SkeletonLine width="70%" height="12px" />
  </SidebarItemWrapper>
);

/* ── App Skeleton Layout (전체 화면 로딩용) ── */

gsap.registerPlugin(ScrambleTextPlugin);

const AppSkeletonWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const ScrambleText = styled.span`
  font-family: 'Geist Mono', 'SF Mono', 'Fira Code', 'Courier New', monospace;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: 0.2em;
  color: #FF6B00;
  text-transform: uppercase;
  user-select: none;
  min-width: 10ch;
  text-align: center;
  display: inline-block;
`;

export const AppSkeleton: React.FC = () => {
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    el.textContent = '';

    const tl = gsap.timeline({ repeat: -1 });
    tl.to(el, {
      scrambleText: {
        text: 'LOADING',
        chars: 'upperCase',
        revealDelay: 0.1,
        speed: 0.8,
      },
      duration: 0.8,
      ease: 'none',
    }).to(el, {
      scrambleText: {
        text: '',
        chars: 'upperCase',
        speed: 1,
      },
      duration: 0.3,
      ease: 'none',
      delay: 0.5,
    });

    return () => { tl.kill(); };
  }, []);

  return (
    <AppSkeletonWrap>
      <ScrambleText ref={textRef} />
    </AppSkeletonWrap>
  );
};
const FilePreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px 16px;
`;

export const FilePreviewSkeleton: React.FC = () => (
  <FilePreviewWrapper>
    <Base $width="min(100%, 400px)" $height="520px" $radius="4px" />
    <Base $width="min(100%, 400px)" $height="520px" $radius="4px" />
  </FilePreviewWrapper>
);

